/**
 * "Where did this number come from?"
 *
 * Opened by clicking any figure in the reporting UI. It re-requests the same
 * slice from the API and shows the individual transactions that produced it,
 * with the reported total displayed alongside — so the reconciliation is
 * visible to the user rather than merely asserted in a test.
 *
 * The total shown is the total over *every* matching row, not the page, which
 * is why it stays constant while paging. That distinction is the whole point:
 * a figure whose meaning changes when you scroll is not auditable.
 */

import type { ScopeFilter } from '../../lib/locationContext';
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReceiptText } from 'lucide-react';
import type {
  DrilldownResult,
  ReportDimension,
  ReportPeriod,
} from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { count, dateTime, money } from '../../lib/format';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Pagination,
  Spinner,
  cn,
} from '../../components/ui';

export interface DrilldownTarget {
  /** What the user clicked, for the modal title. */
  label: string;
  kind: DrilldownResult['kind'];
  dimension?: ReportDimension | undefined;
  key?: string | undefined;
  /** Restrict a sales drill-down to a customer cohort. */
  customers?: 'all' | 'new' | 'returning' | undefined;
  /** The figure that was clicked, so the two can be shown side by side. */
  expectedAmount?: string | undefined;
  /** For count-based tiles, the number that must reconcile instead. */
  expectedCount?: number | undefined;
  /** Plain-English note explaining what this slice contains. */
  note?: string | undefined;
}

interface Props {
  target: DrilldownTarget | null;
  period: ReportPeriod;
  from?: string | undefined;
  to?: string | undefined;
  /** The sidebar scope the report that opened this was read through. */
  scope?: ScopeFilter | undefined;
  /**
   * The currency the report that opened this is stated in. Passed straight
   * back so the rows are the same slice the figure was: filtered differently,
   * the total here could not reconcile against the figure that was clicked,
   * and the mismatch banner below would fire on a difference that was the
   * client's fault rather than a defect.
   */
  currencyCode?: string | undefined;
  onClose: () => void;
}

const PAGE_SIZE = 25;

/** What the Amount column means for each kind of drill-down. */
const AMOUNT_HEADING: Record<DrilldownResult['amountMeaning'], string> = {
  sale_value: 'Sale amount',
  expense_value: 'Expense',
  payment_received: 'Received',
  outstanding_balance: 'Still owed',
  signed_contribution: 'Contribution',
  gross_margin: 'Margin',
};

const TOTAL_HEADING: Record<DrilldownResult['amountMeaning'], string> = {
  sale_value: 'Total revenue',
  expense_value: 'Total expenses',
  payment_received: 'Total received',
  outstanding_balance: 'Total outstanding',
  signed_contribution: 'Net profit',
  gross_margin: 'Gross profit',
};

const MEANING_NOTE: Record<DrilldownResult['amountMeaning'], string | null> = {
  sale_value: null,
  expense_value: null,
  payment_received: 'Payments banked in this period, whatever date the sale itself carries.',
  outstanding_balance:
    'The unpaid remainder of each sale, not its full value — so partly-paid sales appear with only what is still owed.',
  signed_contribution:
    'Sales count positively and expenses negatively, so these rows add up to exactly revenue minus expenses.',
  gross_margin:
    'Only sales with a cost price recorded appear here. Each row shows amount minus cost, which is why this total covers part of revenue rather than all of it.',
};

export function DrilldownModal({
  target,
  period,
  from,
  to,
  scope,
  currencyCode,
  onClose,
}: Props): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const [page, setPage] = useState(1);

  const timezone = business?.timezone ?? 'UTC';

  // Opening a different figure must not inherit the previous one's page.
  useEffect(() => {
    setPage(1);
  }, [target?.label, target?.kind, target?.key, target?.customers]);

  const query = qs({
    period,
    from,
    to,
    ...scope,
    currencyCode,
    dimension: target?.dimension,
    key: target?.key,
    kind: target?.kind ?? 'sales',
    customers: target?.customers ?? 'all',
    page,
    pageSize: PAGE_SIZE,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['drilldown', business?.businessId, query],
    queryFn: () => api.get<DrilldownResult>(path(`/reports/transactions${query}`)),
    enabled: Boolean(business && target),
  });

  // What these rows are actually denominated in, from the response rather than
  // from the business's default — same reason as the report view.
  const currency = data?.currencyCode ?? currencyCode ?? business?.currencyCode ?? 'USD';

  const amountMismatch =
    data && target?.expectedAmount !== undefined && target.expectedAmount !== data.totalAmount;
  const countMismatch =
    data &&
    target?.expectedCount !== undefined &&
    target.expectedCount !== data.distinctCustomerCount;

  return (
    <Modal
      open={Boolean(target)}
      onClose={onClose}
      title={target ? `${target.label} — transactions` : 'Transactions'}
      size="lg"
    >
      {isPending ? (
        <Spinner label="Loading transactions" />
      ) : error ? (
        <ErrorState message="Could not load these transactions." onRetry={() => refetch()} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
            <div>
              <p className="text-xs font-medium text-slate-600">
                {TOTAL_HEADING[data.amountMeaning]}
              </p>
              <p className="mt-0.5 text-lg font-semibold tabular text-slate-900">
                {money(data.totalAmount, currency)}
              </p>
            </div>
            <div className="text-right text-xs text-slate-500">
              <p>
                {count(data.totalCount)}{' '}
                {data.totalCount === 1 ? 'transaction' : 'transactions'}
                {data.distinctCustomerCount > 0 && (
                  <>
                    {' · '}
                    {count(data.distinctCustomerCount)}{' '}
                    {data.distinctCustomerCount === 1 ? 'customer' : 'customers'}
                  </>
                )}
              </p>
              <p>
                {data.range.from} to {data.range.to}
              </p>
            </div>
          </div>

          {/* What this slice actually contains, in words. Stated for the kinds
              whose amounts are not simply "the sale value", because that is
              precisely where a reader would otherwise assume wrongly. */}
          {(target?.note ?? MEANING_NOTE[data.amountMeaning]) && (
            <p className="mb-3 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-xs leading-relaxed text-slate-600">
              {target?.note ?? MEANING_NOTE[data.amountMeaning]}
            </p>
          )}

          {/*
            Shown only when the drill-down disagrees with the figure that was
            clicked. It should be impossible — the reconciliation suite asserts
            it for every dimension — so if it ever appears, it is a real defect
            and the user should see it rather than quietly act on a wrong number.
          */}
          {(amountMismatch || countMismatch) && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {amountMismatch && (
                <p>
                  These transactions total {money(data.totalAmount, currency)} but the figure you
                  clicked was {money(target!.expectedAmount!, currency)}.
                </p>
              )}
              {countMismatch && (
                <p>
                  These transactions cover {count(data.distinctCustomerCount)} customers but the
                  figure you clicked was {count(target!.expectedCount!)}.
                </p>
              )}
              <p className="mt-1">Please report this — the two should always agree.</p>
            </div>
          )}

          {data.rows.length === 0 ? (
            <EmptyState
              icon={<ReceiptText className="size-6" />}
              title="No transactions"
              description="Nothing matched this slice of the report."
            />
          ) : (
            <>
              <div className="-mx-5 overflow-x-auto">
                <table className="w-full min-w-[64rem] text-sm">
                  <thead>
                    <tr className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2 font-medium">Reference</th>
                      <th className="px-3 py-2 font-medium">Date &amp; time</th>
                      <th className="px-3 py-2 font-medium">Details</th>
                      <th className="px-3 py-2 font-medium">Customer</th>
                      <th className="px-3 py-2 font-medium">Product</th>
                      <th className="px-3 py-2 font-medium">Channel</th>
                      <th className="px-3 py-2 font-medium">Branch</th>
                      <th className="px-3 py-2 font-medium">Staff</th>
                      <th className="px-3 py-2 font-medium">Method</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {AMOUNT_HEADING[data.amountMeaning]}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.rows.map((row) => (
                      <tr key={`${row.id}-${row.reference}`} className="align-top">
                        <td className="whitespace-nowrap px-5 py-2.5 font-medium text-slate-900">
                          {row.reference}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                          {dateTime(row.occurredAt, timezone)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-800">{row.description ?? '—'}</td>
                        <Cell value={row.counterparty} />
                        <Cell value={row.product} unknownAs="Not specified" />
                        <Cell value={row.channel} unknownAs="Unattributed" />
                        <Cell value={row.branch} unknownAs="Unassigned" />
                        <Cell value={row.staff} unknownAs="Unassigned" />
                        <Cell value={row.paymentMethod} />
                        <td className="whitespace-nowrap px-3 py-2.5">
                          {row.paymentStatus ? (
                            <Badge tone={statusTone(row.paymentStatus)}>{row.paymentStatus}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right">
                          <span
                            className={cn(
                              'font-medium tabular',
                              row.amount.startsWith('-') ? 'text-red-700' : 'text-slate-900',
                            )}
                          >
                            {money(row.amount, currency)}
                          </span>
                          {/* On a sale list, what is still owed is worth seeing
                              beside what was billed. Suppressed on the
                              outstanding list, where the amount already IS the
                              balance and repeating it reads as double-counting. */}
                          {data.amountMeaning === 'sale_value' &&
                            row.amountOutstanding &&
                            row.amountOutstanding !== '0.00' && (
                              <span className="mt-1 block">
                                <Badge tone="warning">
                                  {money(row.amountOutstanding, currency)} owing
                                </Badge>
                              </span>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="-mx-5 -mb-5">
                <Pagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={data.totalCount}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * A cell whose blank state is named rather than left as a dash.
 *
 * "—" tells the reader the field is empty; it does not tell them the row is
 * *why* the report shows an "Unassigned" slice. Naming the blank with the same
 * word the breakdown uses closes that loop.
 */
function Cell({
  value,
  unknownAs = '—',
}: {
  value: string | null;
  unknownAs?: string;
}): ReactNode {
  return (
    <td className="whitespace-nowrap px-3 py-2.5">
      {value ? (
        <span className="text-slate-700">{value}</span>
      ) : (
        <span className="italic text-slate-400">{unknownAs}</span>
      )}
    </td>
  );
}

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'Paid') return 'success';
  if (status === 'Partially Paid') return 'warning';
  if (status === 'Unpaid') return 'danger';
  return 'neutral';
}
